import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  CheckCircle2,
  Circle,
  Loader2,
  Send,
  Sparkles,
  RotateCcw,
  Check,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useConnectedAccounts } from '@/hooks/useConnectedAccounts';
import { getPlatformDisplayName } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

// ── Limits and config ────────────────────────────────────────────────────────

const CHAR_LIMITS: Record<string, number> = {
  twitter:  280,
  bluesky:  300,
  reddit:  40000,
};

// Platforms that can receive posts. Others (YouTube) are read-only.
const POSTABLE = new Set(['bluesky', 'twitter', 'reddit']);

// Platforms that need the user to reconnect (missing write scope)
const NEEDS_RECONNECT = new Set<string>();

// ── Types ────────────────────────────────────────────────────────────────────

interface PlatformResult {
  platform: string;
  success: boolean;
  url?: string;
  error?: string;
}

interface ComposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ComposeDialog({ open, onOpenChange }: ComposeDialogProps) {
  const navigate = useNavigate();
  const { accounts } = useConnectedAccounts();

  const [text, setText] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(new Set());
  const [redditTitle, setRedditTitle] = useState('');
  const [redditSub, setRedditSub] = useState('');
  const [posting, setPosting] = useState(false);
  const [results, setResults] = useState<PlatformResult[] | null>(null);

  // AI assist state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMode, setAiMode] = useState<'draft' | 'improve' | 'hashtags'>('draft');
  const [aiTopic, setAiTopic] = useState('');
  const [aiResult, setAiResult] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Reset when dialog closes; on open pick up any reply-draft prefill
  useEffect(() => {
    if (!open) {
      setText('');
      setSelectedPlatforms(new Set());
      setRedditTitle('');
      setRedditSub('');
      setResults(null);
      setAiOpen(false);
      setAiResult(null);
      setAiTopic('');
    } else {
      const prefill = sessionStorage.getItem('compose_prefill');
      if (prefill) {
        setText(prefill);
        sessionStorage.removeItem('compose_prefill');
      }
    }
  }, [open]);

  // Connected accounts that support posting
  const postableAccounts = useMemo(
    () =>
      accounts.filter(
        (a) =>
          a.is_active &&
          a.account_username &&
          !a.account_username.startsWith('temp_') &&
          POSTABLE.has(a.platform),
      ),
    [accounts],
  );

  const toggle = (platform: string) => {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  };

  const needsReddit = selectedPlatforms.has('reddit');

  // Character limit for the most restrictive selected platform
  const charLimit = useMemo(() => {
    let min = Infinity;
    for (const p of selectedPlatforms) {
      const lim = CHAR_LIMITS[p];
      if (lim && lim < min) min = lim;
    }
    return min === Infinity ? null : min;
  }, [selectedPlatforms]);

  const charsLeft = charLimit !== null ? charLimit - text.length : null;
  const overLimit = charsLeft !== null && charsLeft < 0;

  const canPost =
    !posting &&
    text.trim().length > 0 &&
    !overLimit &&
    selectedPlatforms.size > 0 &&
    (!needsReddit || (redditTitle.trim().length > 0 && redditSub.trim().length > 0));

  const handlePost = async () => {
    if (!canPost) return;
    setPosting(true);
    setResults(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { toast.error('Please sign in first'); return; }

      const platforms = Array.from(selectedPlatforms);
      const body: Record<string, unknown> = { text: text.trim(), platforms };
      if (needsReddit) {
        body.reddit = {
          title: redditTitle.trim(),
          subreddit: redditSub.trim().replace(/^r\//, ''),
          kind: 'self',
        };
      }

      const { data, error } = await supabase.functions.invoke('post-to-platforms', {
        body,
      });

      if (error) {
        toast.error('Posting failed — please try again');
        return;
      }

      const res: PlatformResult[] = data?.results ?? [];
      setResults(res);

      const succeeded = res.filter((r) => r.success);
      const failed = res.filter((r) => !r.success);

      if (succeeded.length) {
        toast.success(
          `Posted to ${succeeded.map((r) => getPlatformDisplayName(r.platform)).join(', ')}`,
        );
      }
      if (failed.length) {
        toast.error(
          `Failed: ${failed.map((r) => getPlatformDisplayName(r.platform)).join(', ')}`,
        );
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const runAI = async (mode: typeof aiMode) => {
    setAiMode(mode);
    setAiResult(null);
    setAiLoading(true);
    // Use the first selected platform for tone, or bluesky as default
    const platform = Array.from(selectedPlatforms)[0] ?? 'bluesky';
    try {
      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: {
          action: 'compose_assist',
          mode,
          text: mode !== 'draft' ? text : undefined,
          topic: mode === 'draft' ? aiTopic : undefined,
          platform,
        },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error ?? 'AI error');
      setAiResult(data.result as string);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'AI assist failed');
    } finally {
      setAiLoading(false);
    }
  };

  const acceptAI = () => {
    if (!aiResult) return;
    if (aiMode === 'hashtags') {
      setText((t) => (t.trim() ? `${t.trim()} ${aiResult}` : aiResult));
    } else {
      setText(aiResult);
    }
    setAiResult(null);
    setAiOpen(false);
    setAiTopic('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Compose a post</DialogTitle>
        </DialogHeader>

        {results ? (
          // ── Results screen ───────────────────────────────────────────────
          <div className="space-y-4">
            <div className="space-y-2">
              {results.map((r) => (
                <div
                  key={r.platform}
                  className={`flex items-start gap-3 rounded-lg border p-3 ${
                    r.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                  }`}
                >
                  {r.success ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {getPlatformDisplayName(r.platform)}
                    </p>
                    {r.url && (
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        View post ↗
                      </a>
                    )}
                    {r.error && (
                      <p className="text-xs text-red-700 mt-0.5">{r.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setResults(null);
                  setText('');
                  setSelectedPlatforms(new Set());
                  setRedditTitle('');
                  setRedditSub('');
                }}
              >
                Compose another
              </Button>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </div>
          </div>
        ) : (
          // ── Compose screen ───────────────────────────────────────────────
          <div className="space-y-5">
            {/* Platform selector */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Post to</Label>
              {postableAccounts.length === 0 ? (
                <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
                  No compatible accounts connected.{' '}
                  <button
                    className="text-primary underline"
                    onClick={() => { onOpenChange(false); navigate('/dashboard/platforms'); }}
                  >
                    Connect platforms
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {postableAccounts.map((account) => {
                    const selected = selectedPlatforms.has(account.platform);
                    const needsReconnect = NEEDS_RECONNECT.has(account.platform);
                    return (
                      <button
                        key={account.id}
                        onClick={() => !needsReconnect && toggle(account.platform)}
                        title={needsReconnect ? 'Reconnect this account to enable posting' : undefined}
                        className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                          needsReconnect
                            ? 'cursor-not-allowed opacity-50 border-dashed'
                            : selected
                              ? 'border-[#1A2E4A] bg-[#1A2E4A] text-white'
                              : 'border-border hover:border-[#1A2E4A]/50'
                        }`}
                      >
                        {selected ? (
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                        ) : (
                          <Circle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        )}
                        <span>{getPlatformDisplayName(account.platform)}</span>
                        <span className="text-xs opacity-70">{account.account_username}</span>
                        {needsReconnect && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 ml-1">
                            Reconnect
                          </Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {postableAccounts.some((a) => NEEDS_RECONNECT.has(a.platform)) && (
                <p className="text-xs text-muted-foreground mt-2">
                  Reddit and X require reconnecting to grant posting permission.{' '}
                  <button
                    className="text-primary underline"
                    onClick={() => { onOpenChange(false); navigate('/dashboard/platforms'); }}
                  >
                    Reconnect on Platforms page
                  </button>
                </p>
              )}
            </div>

            {/* Reddit extras */}
            {needsReddit && (
              <div className="space-y-3 rounded-lg bg-muted/40 p-3">
                <div>
                  <Label htmlFor="reddit-title" className="text-xs">Post title (required for Reddit)</Label>
                  <Input
                    id="reddit-title"
                    value={redditTitle}
                    onChange={(e) => setRedditTitle(e.target.value)}
                    placeholder="Enter a title for your Reddit post"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="reddit-sub" className="text-xs">Subreddit</Label>
                  <div className="flex items-center mt-1">
                    <span className="text-sm text-muted-foreground border border-r-0 rounded-l-md px-2 py-2 bg-muted">r/</span>
                    <Input
                      id="reddit-sub"
                      value={redditSub}
                      onChange={(e) => setRedditSub(e.target.value.replace(/^r\//, ''))}
                      placeholder="subreddit_name"
                      className="rounded-l-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Text area */}
            <div>
              <Label htmlFor="post-text" className="text-sm font-medium mb-1 block">
                What's on your mind?
              </Label>
              <Textarea
                id="post-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write your post here…"
                rows={5}
                className="resize-none"
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-muted-foreground">
                  {selectedPlatforms.size > 0 && charLimit !== null
                    ? `Shortest limit: ${getPlatformDisplayName(
                        Array.from(selectedPlatforms).find((p) => CHAR_LIMITS[p] === charLimit) ?? '',
                      )} (${charLimit} chars)`
                    : 'Select platforms above'}
                </span>
                {charsLeft !== null && (
                  <span className={`text-xs font-mono ${overLimit ? 'text-red-500' : 'text-muted-foreground'}`}>
                    {charsLeft}
                  </span>
                )}
              </div>
            </div>

            {/* ── AI Assist panel ──────────────────────────────────────── */}
            <div className="rounded-lg border border-border/60 bg-muted/20">
              <button
                type="button"
                onClick={() => { setAiOpen((v) => !v); setAiResult(null); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span className="font-medium">AI assist</span>
                <span className="ml-auto text-[10px]">{aiOpen ? '▲' : '▼'}</span>
              </button>

              {aiOpen && (
                <div className="px-3 pb-3 space-y-2.5 border-t border-border/40 pt-2.5">
                  {/* Mode buttons */}
                  <div className="flex gap-1.5 flex-wrap">
                    {([
                      { mode: 'draft' as const, label: 'Draft from topic' },
                      { mode: 'improve' as const, label: 'Improve my text', disabled: !text.trim() },
                      { mode: 'hashtags' as const, label: 'Suggest hashtags', disabled: !text.trim() },
                    ] as Array<{ mode: typeof aiMode; label: string; disabled?: boolean }>).map(({ mode, label, disabled }) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => { setAiMode(mode); setAiResult(null); }}
                        disabled={disabled}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                          aiMode === mode
                            ? 'border-[#1A2E4A] bg-[#1A2E4A] text-white'
                            : 'border-border/60 text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Topic input for draft mode */}
                  {aiMode === 'draft' && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={aiTopic}
                        onChange={(e) => setAiTopic(e.target.value)}
                        placeholder="What do you want to post about?"
                        className="flex-1 text-xs border rounded-md px-2.5 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-[#1A2E4A]/30"
                        onKeyDown={(e) => { if (e.key === 'Enter') runAI('draft'); }}
                      />
                      <button
                        type="button"
                        onClick={() => runAI('draft')}
                        disabled={aiLoading || !aiTopic.trim()}
                        className="text-xs px-2.5 py-1.5 rounded-md bg-[#1A2E4A] text-white hover:bg-[#1A2E4A]/90 disabled:opacity-50"
                      >
                        {aiLoading ? <RotateCcw className="h-3 w-3 animate-spin" /> : 'Go'}
                      </button>
                    </div>
                  )}

                  {/* One-click for improve / hashtags */}
                  {(aiMode === 'improve' || aiMode === 'hashtags') && !aiResult && (
                    <button
                      type="button"
                      onClick={() => runAI(aiMode)}
                      disabled={aiLoading}
                      className="text-xs px-3 py-1.5 rounded-md bg-[#1A2E4A] text-white hover:bg-[#1A2E4A]/90 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {aiLoading
                        ? <><RotateCcw className="h-3 w-3 animate-spin" /> Working…</>
                        : <><Sparkles className="h-3 w-3" /> {aiMode === 'hashtags' ? 'Suggest hashtags' : 'Improve text'}</>
                      }
                    </button>
                  )}

                  {/* AI result */}
                  {aiResult && (
                    <div className="rounded-md border bg-background p-2.5 space-y-2">
                      <p className="text-xs leading-relaxed">{aiResult}</p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={acceptAI}
                          className="text-[11px] flex items-center gap-1 px-2 py-1 rounded bg-[#1A2E4A] text-white hover:bg-[#1A2E4A]/90"
                        >
                          <Check className="h-3 w-3" />
                          Use this
                        </button>
                        <button
                          type="button"
                          onClick={() => runAI(aiMode)}
                          className="text-[11px] flex items-center gap-1 px-2 py-1 rounded border text-muted-foreground hover:bg-muted"
                        >
                          <RotateCcw className="h-3 w-3" />
                          Try again
                        </button>
                        <button
                          type="button"
                          onClick={() => setAiResult(null)}
                          className="text-[11px] text-muted-foreground hover:text-foreground ml-auto"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handlePost}
                disabled={!canPost}
                className="bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white"
              >
                {posting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {posting
                  ? 'Posting…'
                  : `Post to ${selectedPlatforms.size || '…'} platform${selectedPlatforms.size !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ComposeDialog;
